/**
 * Scrubber -- time-travel control. Walks the BLAST_RADIUS chain
 * history for the selected entity (when one is selected) + lets
 * the operator click a sealed tick to swap `as_of`.
 *
 * When no entity is selected, the history is empty + the scrubber
 * renders a single "now" tick. When the entity has snapshots but
 * the deriver hasn't yet, the response is also empty -- same
 * gracefully-degraded fallback.
 */
import { useMemo } from "react";
import { localeFor } from "shared/time/formatDate";
import { useTranslation } from "react-i18next";

import { useTwinBlastHistory } from "twin/api/constellation_hooks";
import type { ChainSeal, TwinNodeKind } from "shared/api/types";

export interface ScrubberProps {
  selectedKind: TwinNodeKind | null;
  selectedId: string | null;
  asOf: string | null;
  onAsOfChange: (asOf: string | null) => void;
}

/** A tick's moment, short enough to sit in a row of them. */
export function tickLabel(capturedAt: string | null, language?: string): string {
  if (!capturedAt) return "genesis";
  const d = new Date(capturedAt);
  if (Number.isNaN(d.getTime())) return capturedAt.slice(0, 16);
  // F -- `undefined` means "whatever this machine is set to", which is
  // what F3 removed everywhere else.
  return d.toLocaleString(localeFor(language), {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function Scrubber({
  selectedKind,
  selectedId,
  asOf,
  onAsOfChange,
}: ScrubberProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const history = useTwinBlastHistory({
    kind: selectedKind,
    id: selectedId,
    limit: 12,
  });

  const ticks = useMemo<ChainSeal[]>(() => {
    return history.data?.snapshots ?? [];
  }, [history.data]);

  const currentHash = useMemo(() => {
    if (asOf === null && ticks.length > 0) return ticks[0]?.hash ?? null;
    const found = ticks.find((s) => s.captured_at === asOf);
    return found?.hash ?? null;
  }, [asOf, ticks]);

  return (
    <div
      data-testid="constellation-scrubber"
      className="flex items-center gap-3 rounded border border-slate-200 bg-white p-2"
    >
      <button
        type="button"
        data-testid="constellation-scrubber-now"
        onClick={() => onAsOfChange(null)}
        className="rounded border border-brand-brass/40 bg-brand-brass/5 px-2 py-0.5 text-xs text-brand-brassDark hover:bg-brand-brass/10"
      >
        {t("modules.twin.constellation.scrubber.now")}
      </button>
      <div className="flex-1">
        {ticks.length === 0 ? (
          <p
            data-testid="constellation-scrubber-empty"
            className="text-xs italic text-slate-500"
          >
            {t("modules.twin.constellation.scrubber.empty")}
          </p>
        ) : (
          <ol
            data-testid="constellation-scrubber-ticks"
            className="flex items-center gap-2 overflow-x-auto"
          >
            {ticks.map((s) => {
              const active = s.captured_at === asOf;
              return (
                <li key={s.hash ?? "genesis"} className="shrink-0">
                  <button
                    type="button"
                    data-testid="constellation-scrubber-tick"
                    data-captured-at={s.captured_at}
                    data-active={active ? "true" : "false"}
                    onClick={() => onAsOfChange(s.captured_at)}
                    title={`${s.captured_at ?? "genesis"} -- ${s.hash ?? "no hash"}`}
                    className={
                      "inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[11px] " +
                      (active
                        ? "border-brand-brass bg-brand-brass/10 text-brand-brassDark"
                        : "border-slate-200 bg-white text-slate-600 hover:border-brand-brass/40")
                    }
                  >
                    {/* D3 -- the ticks were bare hashes. An operator
                        rewinding a page picks a MOMENT; the hash is
                        how the moment is proven, not how it is
                        chosen. */}
                    <span className="tabular-nums">
                      {tickLabel(s.captured_at, i18n.language)}
                    </span>
                    <span className="font-mono text-[9px] text-slate-400">
                      {shortHash(s.hash)}
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        )}
      </div>
      <div
        data-testid="constellation-scrubber-seal"
        className="text-[10px] uppercase tracking-wide text-slate-500"
      >
        {currentHash
          ? t("modules.twin.constellation.scrubber.sealed", {
              hash: shortHash(currentHash),
            })
          : t("modules.twin.constellation.scrubber.live")}
      </div>
    </div>
  );
}

function shortHash(h: string | null): string {
  if (!h) return "—";
  return h.slice(0, 8);
}
